import { useEffect, useMemo, useState } from 'react';
import { Table, Button, Space, Modal, Input, Empty, Tag, Tooltip } from 'antd';
import {
  EyeOutlined,
  PauseCircleOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  DollarOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { internalAccounts } from '../../api/internalAdminEndpoints';
import { useAppNotifications } from '../../utils/notify';
import LoadingBlock from '../../components/LoadingBlock';
import PageHeader from '../../components/PageHeader';
import { formatDateTime } from '../../utils/format';
import useInternalAdminAuth from '../../contexts/InternalAdminAuthContext';
import StatCard from '../../components/internalAdmin/StatCard';
import {
  CompanyCell,
  ContactCell,
  PlanCell,
  MoneyText,
  paiseToINR
} from '../../components/internalAdmin/AccountCells';

const { TextArea } = Input;

// Stage 5N-2C: approved-accounts page. Read-only listing + Suspend
// action when the operator has accounts.suspend (super_admin only).

export default function ApprovedAccountsPage() {
  const { hasPermission } = useInternalAdminAuth();
  const { notifySuccess, notifyError } = useAppNotifications();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ items: [], total: 0 });

  const [suspendTarget, setSuspendTarget] = useState(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [busy, setBusy] = useState(false);

  const canSuspend = hasPermission('accounts.suspend');

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const resp = await internalAccounts.list({ status: 'active', limit: 50 });
      setData({
        items: Array.isArray(resp.items) ? resp.items : [],
        total: typeof resp.total === 'number' ? resp.total : 0
      });
    } catch (err) {
      setError(err);
      notifyError(err, { title: 'Could not load approved accounts' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function confirmSuspend() {
    if (!suspendTarget) return;
    setBusy(true);
    try {
      await internalAccounts.suspend(suspendTarget.tenantId, {
        reason: suspendReason.trim() || undefined
      });
      notifySuccess(
        'Account suspended',
        `${suspendTarget.legalName || suspendTarget.slug} is now suspended.`
      );
      setSuspendTarget(null);
      setSuspendReason('');
      load();
    } catch (err) {
      notifyError(err, { title: 'Could not suspend account' });
    } finally {
      setBusy(false);
    }
  }

  const stats = useMemo(() => {
    const items = data.items || [];
    let totalPaisePaid = 0;
    for (const it of items) {
      if (it.paidAmountPaise != null) {
        const n = Number(it.paidAmountPaise);
        if (Number.isFinite(n)) totalPaisePaid += n;
      }
    }
    return { approvedCount: data.total, totalPaisePaid };
  }, [data]);

  const columns = useMemo(
    () => [
      { title: 'Company', key: 'company', width: 220, ellipsis: true,
        render: (_v, row) => <CompanyCell row={row} /> },
      { title: 'GSTIN', dataIndex: 'gstin', key: 'gstin', width: 150,
        render: (v) => v ? <code className="text-[11px] text-ink-700">{v}</code> : <span className="text-ink-400">—</span> },
      { title: 'Contact', key: 'contact', width: 220, ellipsis: true,
        render: (_v, row) => <ContactCell row={row} /> },
      { title: 'Plan', key: 'plan', width: 130,
        render: (_v, row) => <PlanCell row={row} /> },
      { title: 'Paid', key: 'paid', width: 110, align: 'right',
        render: (_v, row) => <MoneyText paise={row.paidAmountPaise} /> },
      { title: 'Invoice', key: 'invoice', width: 110,
        render: (_v, row) => row.invoiceNumber
          ? <code className="text-[11px] text-ink-700">{row.invoiceNumber}</code>
          : <span className="text-ink-400">—</span> },
      { title: 'Approved', key: 'approvedAt', width: 160,
        render: (_v, row) => <span className="text-xs text-ink-600">{formatDateTime(row.approvedAt || row.createdAt)}</span> },
      { title: 'Actions', key: 'actions', width: 200, fixed: 'right',
        render: (_v, row) => (
          <Space size={4} wrap={false}>
            <Button size="small" icon={<EyeOutlined />}
              onClick={() => navigate(`/internal/accounts/${row.tenantId}`)}>
              View
            </Button>
            <Tooltip title={canSuspend ? '' : 'You do not have accounts.suspend'}>
              <Button size="small" danger icon={<PauseCircleOutlined />}
                disabled={!canSuspend}
                onClick={() => { setSuspendReason(''); setSuspendTarget(row); }}>
                Suspend
              </Button>
            </Tooltip>
          </Space>
        ) }
    ],
    [canSuspend, navigate]
  );

  return (
    <div>
      <PageHeader
        title="Approved Accounts"
        subtitle="Tenants with active sign-in access. Suspend an account here if a review issue is found post-approval."
        actions={
          <Space>
            <Tag color="green" className="!text-sm !px-3 !py-1">{data.total} active</Tag>
            <Button icon={<ReloadOutlined />} onClick={load} disabled={loading}>Refresh</Button>
          </Space>
        }
      />

      <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 mb-5 max-w-2xl">
        <StatCard label="Approved" value={stats.approvedCount} tone="green"
          icon={<CheckCircleOutlined />} hint="With sign-in access" />
        <StatCard label="Total paid" value={paiseToINR(stats.totalPaisePaid)} tone="blue"
          icon={<DollarOutlined />} hint="Across loaded rows" />
      </div>

      {loading ? <LoadingBlock tip="Loading approved accounts…" /> : (
        <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden">
          <Table
            rowKey="tenantId"
            dataSource={data.items}
            columns={columns}
            size="middle"
            pagination={false}
            scroll={{ x: 1180 }}
            tableLayout="fixed"
            locale={{ emptyText: error
              ? <div className="py-12 text-center text-ink-500 text-sm">Could not load approved accounts.</div>
              : <div className="py-10">
                  <Empty
                    description={
                      <div className="text-ink-700">
                        <p className="text-sm font-semibold m-0">No approved accounts yet</p>
                        <p className="text-xs text-ink-500 m-0 mt-1">
                          Tenants you approve from the pending list will land here.
                        </p>
                      </div>
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                </div>
            }}
          />
        </div>
      )}

      <Modal
        open={Boolean(suspendTarget)}
        title="Suspend account?"
        okText="Suspend account"
        okButtonProps={{ danger: true, loading: busy }}
        cancelButtonProps={{ disabled: busy }}
        onOk={confirmSuspend}
        onCancel={() => { if (busy) return; setSuspendTarget(null); setSuspendReason(''); }}
        maskClosable={!busy}
        destroyOnHidden
      >
        <p className="m-0 mb-2">
          Suspending <strong>{suspendTarget && (suspendTarget.legalName || suspendTarget.slug)}</strong> will block sign-in immediately. Reactivate from the Suspended Accounts page when ready.
        </p>
        <label className="text-xs uppercase tracking-wider text-ink-500 font-semibold">
          Reason (optional)
        </label>
        <TextArea
          value={suspendReason}
          onChange={(e) => setSuspendReason(e.target.value)}
          rows={2}
          maxLength={1000}
          placeholder="Optional context for the audit trail"
          disabled={busy}
        />
      </Modal>
    </div>
  );
}
